import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  ProviderDashboard as ProviderDashboardComponent, 
  type ProviderPatient as Patient,
  ProviderPatientInformationDialog,
  ProviderAccountDialog,
  SupportFeedbackDialog,
  type ProviderVisit,
  Loader
} from '@joinomu/ui'
import { useAuth } from '@/hooks/useAuth'
import { authService, emailService } from '@joinomu/shared'
// import { supabase } from '@/utils/supabase/client'

export function ProviderDashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [activeItem, setActiveItem] = useState('Dashboard')
  const [providerData, setProviderData] = useState<any>(null)
  const [assignedPatients, setAssignedPatients] = useState<Patient[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedPatient, setSelectedPatient] = useState<any | null>(null)
  const [isLoadingPatient, setIsLoadingPatient] = useState(false)
  const [dialogInitialTab, setDialogInitialTab] = useState<string | undefined>(undefined)
  const [dialogInitialVisitId, setDialogInitialVisitId] = useState<string | undefined>(undefined)
  const [dialogInitialMedicationId, setDialogInitialMedicationId] = useState<string | undefined>(undefined)
  const [accountDialogOpen, setAccountDialogOpen] = useState(false)
  
  // Support/Feedback dialog state
  const [supportFeedbackDialogOpen, setSupportFeedbackDialogOpen] = useState(false)
  const [supportFeedbackType, setSupportFeedbackType] = useState<'support' | 'feedback'>('support')

  useEffect(() => {
    const fetchProviderData = async () => {
      if (user?.id) {
        // Use the working role detection to check if user is a provider
        const userRole = await authService.getUserRole()
        
        if (userRole !== 'provider') {
          // User is not a provider, redirect to home
          navigate('/')
          setLoading(false)
          return
        }

        // Get combined provider and profile data
        const { data: providerData, error: providerError } = await authService.getUserProfile()

        if (providerData && !providerError) {
          setProviderData(providerData)
        } else {
          console.warn('Provider data not found:', providerError)
          // Fallback if no data found
          const fallbackData = {
            id: 'provider-' + user.id,
            profile_id: user.id,
            first_name: 'Provider',
            last_name: 'User',
            email: user.email,
            specialty: 'General Practice'
          }
          setProviderData(fallbackData)
        }

        // Fetch assigned patients using the auth service function with medications
        if (user?.id) {
          try {
            console.log('Fetching assigned patients for provider profile ID:', user.id)
            const { data: patientsData, error: patientsError } = await authService.getAssignedPatients(user.id)

            if (!patientsError && patientsData) {
              console.log('Patients data fetched from auth service:', patientsData)
              console.log('First patient medications raw data:', patientsData[0]?.medications)
              
              // Transform the data to match the UI Patient interface
              const transformedPatients: Patient[] = patientsData.map((patient: any) => {
                console.log(`Transforming patient ${patient.first_name}: medications =`, patient.medications)
                console.log(`🔍 APPOINTMENT DEBUG: Patient ${patient.first_name} appointment data:`, {
                  previous_appointment: patient.previous_appointment,
                  next_appointment: patient.next_appointment
                })
                console.log(`🔍 ELIGIBILITY DEBUG: Patient ${patient.first_name} eligibility fields:`, {
                  gender: patient.gender,
                  weight: patient.weight,
                  motivations: patient.motivations,
                  weight_loss_goals: patient.weight_loss_goals
                })
                return {
                  // CRITICAL FIX: Include ALL database fields to preserve eligibility data
                  ...patient, // Spread all fields from database first
                  
                  // Then override/add specific provider dashboard fields
                  id: patient.patient_id || patient.id,
                  profile_id: patient.profile_id,
                  first_name: patient.first_name,
                  last_name: patient.last_name,
                  name: `${patient.first_name} ${patient.last_name}`,
                  email: patient.email,
                  phone: patient.phone,
                  date_of_birth: patient.date_of_birth,
                  has_completed_intake: patient.has_completed_intake,
                  careTeam: [`Dr. ${providerData?.first_name || 'Provider'} ${providerData?.last_name || 'User'}`],
                  treatments: [patient.treatment_type?.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'General Care'],
                  medications: patient.medications || [],
                  status: ['Active'],
                  treatmentType: patient.treatment_type,
                  assigned_date: patient.assigned_date,
                  assignedDate: patient.assigned_date,
                  isPrimary: patient.is_primary,
                  created_at: patient.created_at,
                  // CRITICAL FIX: Include appointment data for Previous/Next Visit columns
                  previous_appointment: patient.previous_appointment,
                  next_appointment: patient.next_appointment
                }
              })
              
              console.log('Transformed patients with medications:', transformedPatients)
              console.log('First transformed patient medications:', transformedPatients[0]?.medications)
              console.log('🔍 PROVIDER DASHBOARD: Setting assigned patients for approvals check:', transformedPatients.map(p => ({ 
                id: p.id, 
                name: p.name, 
                profile_id: p.profile_id,
                has_completed_intake: p.has_completed_intake
              })))
              setAssignedPatients(transformedPatients)
            } else {
              console.log('No patients found from auth service:', patientsError)
              setAssignedPatients([])
            }
          } catch (patientsError) {
            console.log('Error fetching patients from auth service:', patientsError)
            setAssignedPatients([])
          }
        }
      }
      setLoading(false)
    }

    fetchProviderData()
  }, [user, navigate])

  const handleSignOut = async () => {
    try {
      console.log('🔄 Provider dashboard: Starting sign out...')
      await authService.signOut()
      console.log('✅ Provider dashboard: Sign out successful, navigating to home')
      navigate('/')
    } catch (error) {
      console.error('❌ Provider dashboard: Error signing out:', error)
      // Force navigation even if sign out fails
      navigate('/')
    }
  }

  const handleNavigate = (item: string) => {
    setActiveItem(item)
  }

  const handleAccountClick = () => {
    setAccountDialogOpen(true)
  }

  const handleSupportFeedbackClick = (type: "Support" | "Feedback") => {
    console.log('🎯 Provider handleSupportFeedbackClick called with type:', type)
    setSupportFeedbackType(type.toLowerCase() as 'support' | 'feedback')
    setSupportFeedbackDialogOpen(true)
  }

  const handleSupportFeedbackSubmit = async (message: string, type: 'support' | 'feedback') => {
    if (!user?.email) {
      return { 
        success: false, 
        error: 'User email not available' 
      }
    }

    try {
      const result = await emailService.sendSupportFeedback({
        type,
        message,
        userEmail: user.email,
        userName: user.user_metadata?.full_name || user.email.split('@')[0] || 'Anonymous Provider'
      })

      return result
    } catch (error) {
      console.error('Failed to send support/feedback message:', error)
      return {
        success: false,
        error: 'Failed to send message'
      }
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader />
      </div>
    )
  }

  if (!providerData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-bold">Access Denied</h1>
          <p className="text-muted-foreground">You don't have provider privileges.</p>
          <button onClick={() => navigate('/')} className="px-4 py-2 bg-primary text-primary-foreground rounded">Return to Home</button>
        </div>
      </div>
    )
  }

  const handlePatientClickFull = async (patient: Patient, skipInitialTabClear = false) => {
    // Prevent multiple simultaneous calls
    if (isLoadingPatient) {
      console.log('🔄 Already loading patient, ignoring click')
      return
    }
    
    console.log('🔍 PATIENT CLICK: skipInitialTabClear:', skipInitialTabClear)
    console.log('🔍 PATIENT CLICK: Current initial values:', {
      dialogInitialTab,
      dialogInitialVisitId,
      dialogInitialMedicationId
    })
    
    // Clear any initial tab/ID settings when opening from patient table
    // This ensures clicks from the patient table always start at "Patient Information"
    // Only clear these if they weren't already set by another handler (like handleVisitClick)
    if (!skipInitialTabClear) {
      console.log('🔍 PATIENT CLICK: Clearing initial tab settings')
      setDialogInitialTab(undefined)
      setDialogInitialVisitId(undefined)
      setDialogInitialMedicationId(undefined)
    } else {
      console.log('🔍 PATIENT CLICK: Preserving initial tab settings')
    }
    
    // Fetch patient medication preferences and appointments
    try {
      console.log('🔍 Fetching patient data for:', patient.id)
      console.log('🔍 Current dialog state:', dialogOpen)
      setIsLoadingPatient(true)
      
      // Fetch medication preferences using profile_id (auth user ID)
      console.log('🔍 Using patient.profile_id for medication preferences:', patient.profile_id)
      const medicationResult = await authService.getPatientMedicationPreferences(patient.profile_id)
      console.log('🔍 Medication preferences result:', medicationResult)
      const { data: medicationPreferences } = medicationResult
      
      // Fetch patient appointments (visits) using the profile_id from patient data
      console.log('🔍 Fetching patient appointments for visits...')
      console.log('🔍 Patient object:', patient)
      console.log('🔍 Available patient.profile_id:', patient.profile_id)
      
      let visits: any[] = []
      try {
        // Use the profile_id that's already available from the patient data
        const patientProfileId = patient.profile_id
        console.log('🔍 Using patient.profile_id for appointments:', patientProfileId)
        
        const appointmentsResult = await authService.getPatientAppointments(patientProfileId, false)
        console.log('🔍 Patient appointments result:', appointmentsResult)
        
        if (appointmentsResult.data) {
          // Convert appointments to PatientVisit format and sort by most recent first
          visits = appointmentsResult.data
            .map((appt: any) => {
              // Debug: Log ALL keys to see exactly what Supabase returns
              console.log('🔍 VISIT MAPPING: ALL appointment keys:', Object.keys(appt))
              console.log('🔍 VISIT MAPPING: Full appointment:', JSON.stringify(appt, null, 2))
              console.log('🔍 VISIT MAPPING: Prescription filled value:', appt['Prescription filled'], '| Type:', typeof appt['Prescription filled'])
              return {
                id: appt.appointment_id || appt.id,  // Use appointment_id from RPC result
                appointment_date: appt.appointment_date,
                start_time: appt.start_time,
                appointment_type: appt.appointment_type || 'consultation',
                treatment_type: appt.treatment_type,
                status: appt.status,
                provider_notes: appt.provider_notes,
                patient_notes: appt.patient_notes,
                prescription_filled: appt['Prescription filled'] === true
              }
            })
            .sort((a: any, b: any) => {
              // Sort by date descending, then by time descending (most recent first)
              const dateCompare = new Date(b.appointment_date).getTime() - new Date(a.appointment_date).getTime()
              if (dateCompare === 0) {
                // Same date, compare times
                return b.start_time.localeCompare(a.start_time)
              }
              return dateCompare
            })
          console.log(`✅ Converted and sorted ${visits.length} appointments (most recent first)`)
        }
      } catch (appointmentError) {
        console.warn('⚠️ Error fetching patient appointments:', appointmentError)
        // Continue without appointments - they'll show as "no visits"
      }
      
      // Convert Patient to ProviderPatientData format including medication preferences and visits
      // All eligibility fields are now included via the fixed getAssignedPatients method
      const providerPatientData: any = {
        ...patient,
        dateOfBirth: patient.date_of_birth,
        patientId: patient.id,
        medicationPreferences: medicationPreferences || [],
        visits: visits
      }
      
      console.log('✅ ELIGIBILITY FIX: Patient data includes eligibility fields:', {
        patientId: providerPatientData.patientId,
        hasGender: !!providerPatientData.gender,
        hasState: !!providerPatientData.selected_state,
        hasWeight: !!providerPatientData.weight,
        hasMotivations: !!providerPatientData.motivations
      })
      console.log('🔄 Setting selected patient and opening dialog...')
      setSelectedPatient(providerPatientData)
      setDialogOpen(true)
      console.log('🔄 Dialog should be open now')
    } catch (error) {
      console.error('❌ Error fetching patient data:', error)
      // Fallback to basic patient data without preferences or visits
      // CRITICAL FIX: Include ALL patient fields for eligibility section display
      const providerPatientData: any = {
        // Spread all original patient fields to include eligibility data
        ...patient,
        // Override/add specific fields needed by provider dialog
        patientId: patient.id,
        medicationPreferences: [],
        visits: [],
        // ELIGIBILITY FIX: Transform array fields to display strings
        side_effect_guidance: Array.isArray((patient as any).side_effect_guidance) 
          ? (patient as any).side_effect_guidance.join(', ')
          : (patient as any).side_effect_guidance,
        challenges: Array.isArray((patient as any).challenges)
          ? (patient as any).challenges.join(', ')
          : (patient as any).challenges
      }
      setSelectedPatient(providerPatientData)
      setDialogOpen(true)
    } finally {
      console.log('🔄 Resetting isLoadingPatient to false')
      setIsLoadingPatient(false)
    }
  }

  const handleMedicationUpdate = async (medicationId: string, updates: {
    status?: string
    dosage?: string
    frequency?: string
    providerNotes?: string
    faxed?: boolean
    supplyDays?: number
    refills?: 'visit' | 'async'
    medicationId?: string // Add support for changing the actual medication
  }) => {
    try {
      // Get current medication preference before update
      const currentPreference = selectedPatient?.medicationPreferences?.find(
        (pref: any) => pref.id === medicationId
      )

      console.log('🔍 Provider medication update:', {
        medicationId,
        updates,
        currentPreference: currentPreference ? {
          medication_id: currentPreference.medication_id,
          dosage: currentPreference.dosage,
          supply_days: currentPreference.supply_days,
          stripe_subscription_id: currentPreference.stripe_subscription_id
        } : 'Not found'
      })

      // Update the medication preference (pass provider ID for approval/order creation)
      const result = await authService.updateMedicationPreference(medicationId, updates, providerData?.id)

      if (result.success) {
        // Check if changes require subscription update
        // Only trigger subscription changes for actual medication/dosage/supply changes
        const hasSubscriptionChange = (
          (updates.medicationId && updates.medicationId !== currentPreference?.medication_dosage_id) ||
          (updates.supplyDays && updates.supplyDays !== currentPreference?.supply_days) ||
          (updates.dosage && updates.dosage !== currentPreference?.dosage)
        )

        console.log('🔍 Subscription change analysis:', {
          hasSubscriptionChange,
          hasActiveSubscription: !!currentPreference?.stripe_subscription_id,
          medicationDosageChanged: updates.medicationId !== currentPreference?.medication_dosage_id,
          supplyDaysChanged: updates.supplyDays !== currentPreference?.supply_days,
          dosageChanged: updates.dosage !== currentPreference?.dosage,
          currentDosageId: currentPreference?.medication_dosage_id,
          newDosageId: updates.medicationId
        })

        // Trigger automatic subscription change if patient has active subscription
        if (hasSubscriptionChange && currentPreference?.stripe_subscription_id) {
          console.log('🔄 Provider medication change detected, triggering automatic subscription update...')
          
          try {
            // Get the actual patient database ID from the selected patient
            // selectedPatient should have both profile_id (auth user ID) and id (database patient ID)
            const databasePatientId = selectedPatient.id // This should be the actual patients table ID
            
            // Determine new and old dosage IDs for subscription change
            // Only use updates.medicationId if it's different from current AND valid
            const newMedicationDosageId = (updates.medicationId && updates.medicationId !== currentPreference.medication_dosage_id) 
              ? updates.medicationId 
              : currentPreference.medication_dosage_id
            const oldMedicationDosageId = currentPreference.medication_dosage_id
            
            console.log('🔄 Calling medication change API directly:', {
              patientId: databasePatientId, // Use database patient ID, not profile_id
              medicationPreferenceId: medicationId, // The medication preference ID being updated
              newMedicationDosageId,
              oldMedicationDosageId,
              changeReason: `Provider changed medication - ${updates.providerNotes || 'Clinical adjustment'}`,
              changedBy: 'provider'
            })

            // Call the medication change API directly
            const response = await fetch('/api/medication/change-subscription', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                patientId: databasePatientId, // Use database patient ID
                medicationPreferenceId: medicationId, // Pass the preference ID
                newMedicationDosageId,
                oldMedicationDosageId,
                changeReason: `Provider changed medication - ${updates.providerNotes || 'Clinical adjustment'}`,
                changedBy: 'provider'
              })
            })

            const changeResult = await response.json()

            if (response.ok && changeResult.success) {
              console.log('✅ Provider-initiated subscription change completed:', changeResult)
            } else {
              console.error('❌ Provider-initiated subscription change failed:', changeResult.error || 'Unknown error')
            }
          } catch (subscriptionError) {
            console.error('❌ Error during provider-initiated subscription change:', subscriptionError)
          }
        }

        // 🎯 APPROVAL WORKFLOW FIX: Refresh both selected patient AND assigned patients list
        // This ensures ProviderApprovals component sees the updated medication status
        if (selectedPatient) {
          // Find the patient in the assigned patients list to get their profile_id
          const patientInList = assignedPatients.find(p => p.id === selectedPatient.id)
          if (patientInList?.profile_id) {
            // Update the selected patient (for dialog view)
            const { data: medicationPreferences } = await authService.getPatientMedicationPreferences(patientInList.profile_id)
            setSelectedPatient((prev: any) => prev ? {...prev, medicationPreferences: medicationPreferences || []} : null)
            
            // 🎯 CRITICAL FIX: Also update assignedPatients state to trigger ProviderApprovals refresh
            console.log('🔄 APPROVAL FIX: Refreshing assigned patients to update approvals list immediately')
            if (user?.id) {
              const { data: patientsData, error: patientsError } = await authService.getAssignedPatients(user.id)
              if (!patientsError && patientsData) {
                // Transform the data the same way as in the initial useEffect
                const transformedPatients: Patient[] = patientsData.map((patient: any) => ({
                  ...patient,
                  id: patient.patient_id || patient.id,
                  profile_id: patient.profile_id,
                  first_name: patient.first_name,
                  last_name: patient.last_name,
                  name: `${patient.first_name} ${patient.last_name}`,
                  email: patient.email,
                  phone: patient.phone,
                  date_of_birth: patient.date_of_birth,
                  has_completed_intake: patient.has_completed_intake,
                  careTeam: [`Dr. ${providerData?.first_name || 'Provider'} ${providerData?.last_name || 'User'}`],
                  treatments: [patient.treatment_type?.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'General Care'],
                  medications: patient.medications || [],
                  status: ['Active'],
                  treatmentType: patient.treatment_type,
                  assigned_date: patient.assigned_date,
                  assignedDate: patient.assigned_date,
                  isPrimary: patient.is_primary,
                  created_at: patient.created_at,
                  previous_appointment: patient.previous_appointment,
                  next_appointment: patient.next_appointment
                }))
                
                console.log('✅ APPROVAL FIX: Updated assigned patients - approvals should refresh immediately')
                setAssignedPatients(transformedPatients)
              }
            }
          }
        }
      } else {
        console.error('Error updating medication preference:', result.error?.message || 'Unknown error')
      }
    } catch (error) {
      console.error('Error updating medication preference:', error instanceof Error ? error.message : 'Unknown error')
    }
  }

  // Wrapper function to match the expected signature for the component
  const handlePatientClick = (patient: Patient) => {
    return handlePatientClickFull(patient, false)
  }

  // Note: This function preserved for potential future approval workflows
  // Currently not used by UI components after approval tab removal  
  /*
  const handleApprovalAction = async (preferenceId: string, action: 'approve' | 'deny') => {
    try {
      // Update the medication preference status
      const updates = {
        status: action === 'approve' ? 'approved' : 'denied',
        providerNotes: action === 'approve' ? 'Approved by provider' : 'Denied by provider'
      }

      const result = await authService.updateMedicationPreference(preferenceId, updates, providerData?.id)

      if (result.success) {
        // 🎯 REFILL ORDER CREATION FIX: Create fax record when approving refills to trigger order creation
        if (action === 'approve' && providerData?.profile_id) {
          console.log('🔄 REFILL APPROVAL: Creating fax record to trigger order creation for preference:', preferenceId)
          
          try {
            // Get the specific patient for this medication preference
            const preferenceResult = await authService.getPatientMedicationPreferences(preferenceId)
            if (preferenceResult.data && preferenceResult.data.length > 0) {
              const preference = preferenceResult.data[0]
              
              // Find the patient that owns this preference
              const patient = assignedPatients.find(p => 
                p.id === preference.patient_id || 
                p.profile_id === preference.patient_profile_id
              )
              
              if (patient?.profile_id) {
                const faxResult = await authService.createMedicationApprovalAndFax(
                  preferenceId,
                  providerData.profile_id,
                  patient.profile_id
                )
                
                if (faxResult.error) {
                  console.error('❌ Failed to create fax record for refill approval:', faxResult.error)
                } else {
                  console.log('✅ Successfully created fax record for refill approval - order should be created automatically')
                }
              } else {
                console.warn('⚠️ Could not find patient in assigned patients list for preference:', preferenceId)
              }
            } else {
              console.warn('⚠️ Could not find preference details for ID:', preferenceId)
            }
          } catch (faxError) {
            console.error('❌ Exception creating fax record for refill approval:', faxError)
          }
        }
        // 🎯 APPROVAL WORKFLOW FIX: Refresh assigned patients immediately after approval action
        console.log('🔄 APPROVAL ACTION: Refreshing assigned patients to update approvals list')
        if (user?.id) {
          const { data: patientsData, error: patientsError } = await authService.getAssignedPatients(user.id)
          if (!patientsError && patientsData) {
            // Transform the data the same way as in the initial useEffect
            const transformedPatients: Patient[] = patientsData.map((patient: any) => ({
              ...patient,
              id: patient.patient_id || patient.id,
              profile_id: patient.profile_id,
              first_name: patient.first_name,
              last_name: patient.last_name,
              name: `${patient.first_name} ${patient.last_name}`,
              email: patient.email,
              phone: patient.phone,
              date_of_birth: patient.date_of_birth,
              has_completed_intake: patient.has_completed_intake,
              careTeam: [`Dr. ${providerData?.first_name || 'Provider'} ${providerData?.last_name || 'User'}`],
              treatments: [patient.treatment_type?.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'General Care'],
              medications: patient.medications || [],
              status: ['Active'],
              treatmentType: patient.treatment_type,
              assigned_date: patient.assigned_date,
              assignedDate: patient.assigned_date,
              isPrimary: patient.is_primary,
              created_at: patient.created_at,
              previous_appointment: patient.previous_appointment,
              next_appointment: patient.next_appointment
            }))
            
            console.log('✅ APPROVAL ACTION: Updated assigned patients - approved medications should disappear immediately')
            setAssignedPatients(transformedPatients)
          }
        }
      } else {
        console.error('Error updating medication approval:', result.error?.message || 'Unknown error')
      }
    } catch (error) {
      console.error('Error updating medication approval:', error instanceof Error ? error.message : 'Unknown error')
    }
  }
  */

  const handleVisitClick = async (visit: ProviderVisit) => {
    console.log('Visit clicked:', visit)
    
    // Prevent multiple simultaneous calls
    if (isLoadingPatient) {
      console.log('🔄 Already loading patient for visit, ignoring click')
      return
    }
    
    // Find the patient by patient_id from the visit
    const patient = assignedPatients.find(p => p.id === visit.patient_id)
    if (!patient) {
      console.error('Patient not found for visit:', visit)
      return
    }
    
    // Set the initial tab and visit ID before opening dialog
    console.log('🔍 VISIT CLICK: Setting initial tab to Visits and visit ID to:', visit.id)
    setDialogInitialTab('Visits')
    setDialogInitialVisitId(visit.id)
    setDialogInitialMedicationId(undefined)
    
    // Open the patient dialog
    console.log('🔍 VISIT CLICK: Opening patient dialog for:', patient.name)
    await handlePatientClickFull(patient, true) // Skip clearing initial tab settings
  }


  const userData = {
    name: `${providerData.first_name} ${providerData.last_name}`,
    email: user?.email || '',
    avatar: ''
  }

  return (
    <>
      <ProviderDashboardComponent
        user={userData}
        onLogout={handleSignOut}
        activeItem={activeItem}
        showPatientTable={activeItem === 'Patients'}
        onNavigate={handleNavigate}
        providerId={providerData?.profile_id}
        assignedPatients={assignedPatients}
        onPatientClick={handlePatientClick}
        onVisitClick={handleVisitClick}
        onAccountClick={handleAccountClick}
        onSupportFeedbackClick={handleSupportFeedbackClick}
      />
      <ProviderPatientInformationDialog
        open={dialogOpen}
        onOpenChange={(open: boolean) => {
          console.log('🔍 DIALOG: Open state changing to:', open)
          console.log('🔍 DIALOG: Current props when opening:', {
            initialTab: dialogInitialTab,
            initialVisitId: dialogInitialVisitId,
            initialMedicationId: dialogInitialMedicationId,
            patientName: selectedPatient?.name
          })
          setDialogOpen(open)
          if (!open) {
            // Clear state when dialog closes
            setDialogInitialTab(undefined)
            setDialogInitialVisitId(undefined)
            setDialogInitialMedicationId(undefined)
            
            // 🎯 APPROVAL WORKFLOW FIX: Refresh approvals when dialog closes
            // This will cause the ProviderDashboard component to re-fetch approvals,
            // which will automatically filter out any medications that are now 'approved' status
            console.log('🔄 Dialog closed - triggering approvals refresh to remove approved medications')
            
            // Navigate to the approvals tab if coming from a medication approval workflow
            if (dialogInitialMedicationId) {
              console.log('🔄 Medication dialog closed - switching to approvals tab')
              setActiveItem('Dashboard') // Switch to dashboard view to show approvals
              
              // Force refresh assigned patients to pick up medication status changes
              // This will trigger the approvals list to re-fetch and filter out approved medications
              setTimeout(async () => {
                try {
                  console.log('🔄 Refreshing assigned patients to update approvals list')
                  if (user?.id) {
                    const { data: patientsData, error: patientsError } = await authService.getAssignedPatients(user.id)
                    if (!patientsError && patientsData) {
                      // Transform the data the same way as in the initial useEffect
                      const transformedPatients: Patient[] = patientsData.map((patient: any) => ({
                        ...patient,
                        id: patient.patient_id || patient.id,
                        profile_id: patient.profile_id,
                        first_name: patient.first_name,
                        last_name: patient.last_name,
                        name: `${patient.first_name} ${patient.last_name}`,
                        email: patient.email,
                        phone: patient.phone,
                        date_of_birth: patient.date_of_birth,
                        has_completed_intake: patient.has_completed_intake,
                        careTeam: [`Dr. ${providerData?.first_name || 'Provider'} ${providerData?.last_name || 'User'}`],
                        treatments: [patient.treatment_type?.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'General Care'],
                        medications: patient.medications || [],
                        status: ['Active'],
                        treatmentType: patient.treatment_type,
                        assigned_date: patient.assigned_date,
                        assignedDate: patient.assigned_date,
                        isPrimary: patient.is_primary,
                        created_at: patient.created_at,
                        previous_appointment: patient.previous_appointment,
                        next_appointment: patient.next_appointment
                      }))
                      
                      console.log('✅ Updated assigned patients - approvals should refresh automatically')
                      setAssignedPatients(transformedPatients)
                    }
                  }
                } catch (refreshError) {
                  console.warn('⚠️ Error refreshing assigned patients:', refreshError)
                }
              }, 500) // Small delay to ensure medication status update is complete
            }
          }
        }}
        patient={selectedPatient}
        providerId={providerData?.profile_id}
        initialTab={dialogInitialTab}
        initialVisitId={dialogInitialVisitId}
        initialMedicationId={dialogInitialMedicationId}
        onMedicationUpdate={handleMedicationUpdate}
      />
      <ProviderAccountDialog
        open={accountDialogOpen}
        onOpenChange={setAccountDialogOpen}
      />
      <SupportFeedbackDialog
        open={supportFeedbackDialogOpen}
        onOpenChange={setSupportFeedbackDialogOpen}
        type={supportFeedbackType}
        onSubmit={handleSupportFeedbackSubmit}
      />
    </>
  )
}